import { useEffect, useState, Fragment, useRef } from 'react';
import { MagnifyingGlassIcon, FunnelIcon, CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { Listbox, Transition } from '@headlessui/react';

import BodaccTable from '../components/BodaccTable';
import { 
  fetchCompanyAdherentsBodacc, 
  selectBodaccPage,
  setBodaccPage
} from '../store/slices/bodaccSlice';
import { RootState, AppDispatch } from '../store';
import { BODACC_FAMILY_TYPES } from '../utils/bodaccUtils';
import NotificationPreferences from '../components/NotificationPreferences';
import SurveillanceTabs from '../components/surveillance/SurveillanceTabs';
import SurveillanceFilterForm from '../components/surveillance/SurveillanceFilterForm';
import SurveillanceTable from '../components/surveillance/SurveillanceTable';
import SurveillancePagination from '../components/surveillance/SurveillancePagination';
import {
  selectCurrentTab,
  selectEstablishments,
  selectIsLoading,
  selectCurrentPage,
  selectTotalPages,
  selectTotalItems,
  selectItemsPerPage,
  selectFilters,
  setCurrentPage,
  setFilters,
  fetchEstablishmentChanges,
  SurveillanceTabValues
} from '../store/slices/surveillanceSlice';

const Anomalies = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFamilleAvis, setSelectedFamilleAvis] = useState<string[]>([]);
  
  // Ref to track current request ID
  const currentRequestIdRef = useRef<number>(0);
  
  // Track if initial search has been performed
  const [hasInitialSearch, setHasInitialSearch] = useState(false);
  
  // Track if surveillance data has been loaded to prevent infinite loops
  const [hasSurveillanceLoaded, setHasSurveillanceLoaded] = useState(false);
  
  // Get pagination and sorting state from Redux
  const currentPage = useSelector(selectBodaccPage);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  // Surveillance selectors
  const currentTab = useSelector(selectCurrentTab);
  const surveillanceEstablishments = useSelector(selectEstablishments);
  const surveillanceIsLoading = useSelector(selectIsLoading);
  const surveillanceCurrentPage = useSelector(selectCurrentPage);
  const surveillanceTotalPages = useSelector(selectTotalPages);
  const surveillanceTotalItems = useSelector(selectTotalItems);
  const surveillanceItemsPerPage = useSelector(selectItemsPerPage);
  const surveillanceFilters = useSelector(selectFilters);

  // Initial data load on component mount
  useEffect(() => {
    if (currentUser?.companyId && !hasInitialSearch) {
      // Set initial search flag to true to prevent double fetching
      setHasInitialSearch(true);
      
      // Fetch BODACC data with current filters
      const fetchData = async () => {
        try {
          await dispatch(fetchCompanyAdherentsBodacc({
            companyId: currentUser.companyId.toString(),
            search: searchTerm,
            familleAvis: selectedFamilleAvis.join(',')
          }) as any);
        } catch (error) {
          console.error('Error fetching BODACC data:', error);
        }
      };
      
      fetchData();
    }
  }, [dispatch, currentUser, hasInitialSearch]);
  
  // Handle pagination changes
  useEffect(() => {
    if (currentUser?.companyId && hasInitialSearch && currentPage > 0) {
      console.log('Pagination effect triggered, currentPage:', currentPage);
      
      // Only fetch data for pagination changes
      const fetchData = async () => {
        // Increment request ID to track the latest request
        const requestId = currentRequestIdRef.current + 1;
        currentRequestIdRef.current = requestId;
        
        try {
          await dispatch(fetchCompanyAdherentsBodacc({
            companyId: currentUser.companyId.toString(),
            search: searchTerm,
            familleAvis: selectedFamilleAvis.join(',')
          }) as any);
          
          // Check if this is still the most recent request
          if (currentRequestIdRef.current !== requestId) {
            console.log('Ignoring stale response for request ID:', requestId);
            return;
          }
          
          console.log('Request completed successfully:', requestId);
        } catch (error) {
          console.error('Error fetching BODACC data:', error);
        }
      };
      
      fetchData();
    }
  }, [dispatch, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Search form submitted');
    
    if (currentUser?.companyId) {
      // Increment request ID for the new request
      const requestId = currentRequestIdRef.current + 1;
      currentRequestIdRef.current = requestId;
      
      // Set hasInitialSearch to true
      setHasInitialSearch(true);
      
      // Fetch data with new filters
      const fetchData = async () => {
        try {
          // Reset to first page when searching
          if (currentPage !== 1) {
            dispatch(setBodaccPage(1));
          }
          
          await dispatch(fetchCompanyAdherentsBodacc({
            companyId: currentUser.companyId.toString(),
            search: searchTerm,
            familleAvis: selectedFamilleAvis.join(',')
          }) as any);
          
          // Check if this is still the most recent request
          if (currentRequestIdRef.current !== requestId) {
            console.log('Ignoring stale response for request ID:', requestId);
            return;
          }
          
          console.log('Request completed successfully:', requestId);
        } catch (error) {
          console.error('Error fetching BODACC data:', error);
        }
      };
      
      fetchData();
    }
  };

  const handleFamilleAvisChange = (selected: string[]) => {
    setSelectedFamilleAvis(selected);
    // No longer trigger search on change
  };

  const removeFamilleAvis = (valueToRemove: string) => {
    setSelectedFamilleAvis(selectedFamilleAvis.filter(value => value !== valueToRemove));
    // No longer trigger search on change
  };

  // Surveillance handlers
  const handleSurveillanceFilterSubmit = (filterData: any) => {
    dispatch(setFilters(filterData));
    dispatch(setCurrentPage(1)); // Reset to page 1 when applying new filters
    setHasSurveillanceLoaded(true); // Mark as loaded to prevent useEffect from triggering
    
    // Fetch data with new filters
    dispatch(fetchEstablishmentChanges({
      page: 1,
      limit: surveillanceItemsPerPage,
      fromDate: filterData.fromDate || undefined,
      toDate: filterData.toDate || undefined,
      status: filterData.status === 'all' ? undefined : filterData.status
    }));
  };

  const handleSurveillancePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
    setHasSurveillanceLoaded(true); // Mark as loaded to prevent useEffect from triggering
    
    // Fetch data for the new page
    dispatch(fetchEstablishmentChanges({
      page,
      limit: surveillanceItemsPerPage,
      fromDate: surveillanceFilters.fromDate || undefined,
      toDate: surveillanceFilters.toDate || undefined,
      status: surveillanceFilters.status === 'all' ? undefined : surveillanceFilters.status
    }));
  };

  // Reset surveillance loaded flag when switching tabs
  useEffect(() => {
    if (currentTab === SurveillanceTabValues.BODACC_ANOMALIES) {
      setHasSurveillanceLoaded(false);
    }
  }, [currentTab]);

  // Initial load for surveillance data when tab is switched to SIRET
  useEffect(() => {
    if (currentTab === SurveillanceTabValues.SIRET_SURVEILLANCE && !hasSurveillanceLoaded && !surveillanceIsLoading) {
      setHasSurveillanceLoaded(true);
      dispatch(fetchEstablishmentChanges({
        page: 1,
        limit: surveillanceItemsPerPage,
        status: surveillanceFilters.status === 'all' ? undefined : surveillanceFilters.status
      }));
    }
  }, [currentTab, hasSurveillanceLoaded, surveillanceIsLoading, dispatch, surveillanceItemsPerPage, surveillanceFilters.status]);

  const getTabTitle = () => {
    switch (currentTab) {
      case SurveillanceTabValues.BODACC_ANOMALIES:
        return "Anomalies BODACC";
      case SurveillanceTabValues.SIRET_SURVEILLANCE:
        return "Surveillance SIRET";
      default:
        return "Surveillance";
    }
  };

  const getTabDescription = () => {
    switch (currentTab) {
      case SurveillanceTabValues.BODACC_ANOMALIES:
        return "Consultez les publications BODACC concernant vos adhérents et filtrez par type d'avis pour trouver les informations pertinentes.";
      case SurveillanceTabValues.SIRET_SURVEILLANCE:
        return "Suivez l'historique des ouvertures et fermetures d'établissements de vos adhérents.";
      default:
        return "Surveillance des établissements";
    }
  };

  return (
    <div className="py-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        <h1 className="text-2xl font-semibold text-gray-900">{getTabTitle()}</h1>
        <p className="mt-2 text-sm text-gray-700">
          {getTabDescription()}
        </p>
      </div>
      
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8 mt-6">
        <NotificationPreferences type="anomaly" />
        
        {/* Surveillance Tabs */}
        <SurveillanceTabs />
        
        {/* BODACC Anomalies Tab Content */}
        {currentTab === SurveillanceTabValues.BODACC_ANOMALIES && (
          <div className="bg-white shadow rounded-lg p-6">
            <form onSubmit={handleSearch} className="mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher par SIREN, nom d'entreprise ou mot-clé
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      name="search"
                      id="search"
                      className="block w-full rounded-md border-0 py-2 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Rechercher par SIREN ou par mot-clé (nom d'entreprise, ville, etc.)"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="w-full md:w-64">
                  <label htmlFor="familleAvis" className="block text-sm font-medium text-gray-700 mb-1">
                    Filtrer par famille d'avis
                  </label>
                  <div className="relative">
                    <Listbox value={selectedFamilleAvis} onChange={handleFamilleAvisChange} multiple>
                      <div className="relative">
                        <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-2 pl-10 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <FunnelIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </div>
                          <span className="block truncate">
                            {selectedFamilleAvis.length === 0 
                              ? 'Toutes les familles d\'avis' 
                              : `${selectedFamilleAvis.length} famille(s) sélectionnée(s)`}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {Object.entries(BODACC_FAMILY_TYPES).map(([value, label]) => (
                              <Listbox.Option
                                key={value}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                                  }`
                                }
                                value={value}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {label}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                  
                  {/* Selected items display */}
                  {selectedFamilleAvis.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedFamilleAvis.map((value) => (
                        <span 
                          key={value} 
                          className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                        >
                          {BODACC_FAMILY_TYPES[value as keyof typeof BODACC_FAMILY_TYPES]}
                          <button
                            type="button"
                            onClick={() => removeFamilleAvis(value)}
                            className="ml-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-indigo-600 hover:bg-indigo-200 hover:text-indigo-500 focus:bg-indigo-500 focus:text-white focus:outline-none"
                          >
                            <span className="sr-only">Retirer {BODACC_FAMILY_TYPES[value as keyof typeof BODACC_FAMILY_TYPES]}</span>
                            <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-end">
                  <button
                    type="submit"
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Rechercher
                  </button>
                </div>
              </div>
            </form>
            
            <div className="mt-4">
              <BodaccTable />
            </div>
          </div>
        )}

        {/* SIRET Surveillance Tab Content */}
        {currentTab === SurveillanceTabValues.SIRET_SURVEILLANCE && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Filtres de recherche
              </h3>
              <SurveillanceFilterForm 
                onSubmit={handleSurveillanceFilterSubmit}
                isLoading={surveillanceIsLoading}
              />
            </div>
            
            <SurveillanceTable 
              establishments={surveillanceEstablishments}
              isLoading={surveillanceIsLoading}
            />
            
            {!surveillanceIsLoading && surveillanceEstablishments.length > 0 && (
              <SurveillancePagination 
                currentPage={surveillanceCurrentPage}
                totalPages={surveillanceTotalPages}
                totalItems={surveillanceTotalItems}
                itemsPerPage={surveillanceItemsPerPage}
                onPageChange={handleSurveillancePageChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Anomalies; 